import { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { getProjectTypeColor } from "../../../config/projectTypeColors";

export default function InlineProjectPicker({ projects, currentId, recentIds = [], onSelect, onClose }) {
  const [q, setQ] = useState("");
  const [hlIdx, setHlIdx] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  // Defensive: niemals crashen, auch wenn projects undefined/null oder Einträge unvollständig sind
  const safeProjects = Array.isArray(projects) ? projects.filter(p => p && p.id) : [];
  const safeRecent = Array.isArray(recentIds) ? recentIds : [];
  const active = safeProjects.filter(p => p.is_active !== false);
  const recent = safeRecent.map(id => active.find(p => p.id === id)).filter(Boolean);
  const ql = (q || "").toLowerCase();
  const filtered = ql
    ? active.filter(p => ((p.name || "").toLowerCase().includes(ql)) || ((p.description || "").toLowerCase().includes(ql)))
    : active;
  const showRecent = !q && recent.length > 0;
  const allItems = showRecent ? [...recent, "__sep__", ...filtered.filter(p => !safeRecent.includes(p.id))] : filtered;
  const selectableItems = allItems.filter(x => x !== "__sep__");

  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHlIdx(i => Math.min(i + 1, selectableItems.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setHlIdx(i => Math.max(i - 1, 0)); return; }
    if (e.key === "Enter") { e.preventDefault(); if (selectableItems[hlIdx]) onSelect(selectableItems[hlIdx].id); return; }
  };

  useEffect(() => { setHlIdx(0); }, [q]);
  useEffect(() => { const el = listRef.current?.querySelector(`[data-hl="true"]`); if (el) el.scrollIntoView({ block: "nearest" }); }, [hlIdx]);

  // Empty-State: keine Projekte verfügbar → freundliche Meldung statt leerem Picker
  const noProjectsAvailable = safeProjects.length === 0;

  let selectIdx = -1;
  return (
    <div ref={ref} className="absolute left-0 top-full mt-1 z-50 rounded shadow-lg" style={{ width: 260, background: "var(--tga-bg-card)", border: "1px solid var(--tga-border-strong)" }} data-testid="project-picker">
      <div className="flex items-center gap-1.5 px-2.5 py-2" style={{ borderBottom: "1px solid var(--tga-border)" }}>
        <Search className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--tga-text-dim)" }} />
        <input ref={inputRef} className="w-full text-[12px] bg-transparent border-none outline-none" style={{ color: "var(--tga-text-primary)" }} placeholder="Projekt suchen..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} data-testid="project-picker-search" />
      </div>
      <div ref={listRef} className="overflow-auto" style={{ maxHeight: 240 }}>
        {noProjectsAvailable && (
          <div className="px-3 py-4 text-[11px]" style={{ color: "var(--tga-text-dim)" }} data-testid="project-picker-empty">
            Keine Projekte verfügbar.<br />Bitte wende dich an deinen Projektleiter.
          </div>
        )}
        {!noProjectsAvailable && allItems.length === 0 && <div className="px-3 py-3 text-[11px]" style={{ color: "var(--tga-text-dim)" }}>Kein Projekt gefunden</div>}
        {allItems.map((item, i) => {
          if (item === "__sep__") return (<div key="sep" className="mt-1 pt-1 px-3 pb-0.5" style={{ borderTop: "1px solid var(--tga-border)" }}><span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--tga-text-dim)" }}>Alle Projekte</span></div>);
          if (i === 0 && showRecent) {
            selectIdx++;
            const isHl = selectIdx === hlIdx, isCur = item.id === currentId;
            return (
              <div key={`rh-${item.id}`}>
                <div className="px-3 pt-1.5 pb-0.5"><span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--tga-text-dim)" }} data-testid="recent-projects-label">Zuletzt verwendet</span></div>
                <div className={`px-3 py-1 cursor-pointer transition-colors`} style={{ background: isHl ? "var(--tga-blue-soft)" : isCur ? "var(--tga-bg-hover)" : undefined }} onClick={() => onSelect(item.id)} data-hl={isHl ? "true" : undefined} data-testid={`project-option-${item.id}`}
                  onMouseEnter={(e) => { if (!isHl) e.currentTarget.style.background = "var(--tga-bg-hover)"; }}
                  onMouseLeave={(e) => { if (!isHl && !isCur) e.currentTarget.style.background = "transparent"; }}
                >
                  <div className={`text-[12px] flex items-center gap-1.5`} style={{ color: isCur ? "var(--tga-blue)" : "var(--tga-text-primary)", fontWeight: isCur ? 500 : undefined }}>
                    <span className="inline-block w-2 h-2 shrink-0 rounded-[1px]" style={{ backgroundColor: item.color || getProjectTypeColor(item.project_type) }} />
                    {item.name || item.short_code || "(ohne Name)"}
                  </div>
                  {item.description && <div className="text-[10px] leading-tight pl-3.5" style={{ color: "var(--tga-text-dim)" }}>{item.description}</div>}
                </div>
              </div>
            );
          }
          selectIdx++;
          const isHl = selectIdx === hlIdx, isCur = item.id === currentId;
          return (
            <div key={`${showRecent && i <= recent.length ? "r" : "a"}-${item.id}-${i}`} className={`px-3 py-1 cursor-pointer transition-colors`} style={{ background: isHl ? "var(--tga-blue-soft)" : isCur ? "var(--tga-bg-hover)" : undefined }} onClick={() => onSelect(item.id)} data-hl={isHl ? "true" : undefined} data-testid={`project-option-${item.id}`}
              onMouseEnter={(e) => { if (!isHl) e.currentTarget.style.background = "var(--tga-bg-hover)"; }}
              onMouseLeave={(e) => { if (!isHl && !isCur) e.currentTarget.style.background = "transparent"; }}
            >
              <div className={`text-[12px] flex items-center gap-1.5`} style={{ color: isCur ? "var(--tga-blue)" : "var(--tga-text-primary)", fontWeight: isCur ? 500 : undefined }}>
                <span className="inline-block w-2 h-2 shrink-0 rounded-[1px]" style={{ backgroundColor: item.color || getProjectTypeColor(item.project_type) }} />
                {item.name || item.short_code || "(ohne Name)"}
              </div>
              {item.description && <div className="text-[10px] text-gray-400 leading-tight pl-3.5">{item.description}</div>}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 px-2.5 py-1 text-[9px]" style={{ borderTop: "1px solid var(--tga-border)", color: "var(--tga-text-dim)" }}>
        <span>&#8593;&#8595; Navigieren</span><span>&#8629; Auswaehlen</span><span>Esc Schliessen</span>
      </div>
    </div>
  );
}
